"use client";

import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { usePersonalization } from "@/lib/context/PersonalizationProvider";

const MASK = "linear-gradient(to top,#000 0%,#000 36%,transparent 100%)";

/** Full-bleed, masked scene/wash painted behind the app (personalization). */
export function SceneBackground() {
  const { activeTheme, hydrated } = usePersonalization();
  // Decorative image themes wait for idle so they don't contend with LCP on Slow 4G.
  const imageSrc = hydrated && activeTheme.type === "img" ? activeTheme.img : null;
  const [unlockedSrc, setUnlockedSrc] = useState<string | null>(null);
  // Derived, so switching theme hides the old image without an effect resetting state.
  const imageUnlocked = imageSrc != null && unlockedSrc === imageSrc;

  useEffect(() => {
    if (imageSrc == null) return;

    let cancelled = false;
    const unlock = () => {
      if (!cancelled) setUnlockedSrc(imageSrc);
    };

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(unlock, { timeout: 2500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timer = window.setTimeout(unlock, 1);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [imageSrc]);

  const base: CSSProperties = {
    WebkitMaskImage: MASK,
    maskImage: MASK,
    transition: "opacity 240ms ease",
  };

  let style: CSSProperties;
  if (!hydrated) {
    // No background-image until we know the preference — browsers fetch
    // url() even at opacity:0, which was pulling ~2.5MB on every first paint.
    style = { ...base, opacity: 0 };
  } else if (activeTheme.type === "img") {
    if (!imageUnlocked) {
      style = { ...base, opacity: 0 };
    } else {
      style = {
        ...base,
        backgroundImage: `url('${activeTheme.img}')`,
        backgroundSize: "cover",
        backgroundPosition: "center bottom",
        opacity: 0.28,
        filter: "saturate(0.85)",
      };
    }
  } else if (activeTheme.type === "color") {
    style = { ...base, background: activeTheme.wash, opacity: 0.55 };
  } else {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[66vh]"
      style={style}
    />
  );
}
